<template>
  <div class="container mt-0 mb-4">
    <div class="card shadow-sm p-4 bg-light">
      <!-- Encabezado -->
      <div class="d-flex align-items-center mb-4">
        <button class="btn btn-outline-secondary me-3" @click="volver">
          ← Regresar
        </button>
        <h3 class="m-0 flex-grow-1 text-center">✏️ Ajustar Reserva</h3>
      </div>

      <!-- Datos del huésped -->
      <div v-if="reserva" class="mb-4">
        <h5 class="mb-3">Datos del Huésped</h5>
        <p><strong>Nombre:</strong> {{ reserva.huesped?.Nombre }} {{ reserva.huesped?.Apellido }}</p>
        <p><strong>Código:</strong> R00{{ reserva.IdReserva }}</p>
        <p><strong>Estado:</strong>
          <span class="badge" :class="{
            'bg-warning text-dark': reserva.EstadoReserva === 0,
            'bg-success': reserva.EstadoReserva === 1,
            'bg-secondary': reserva.EstadoReserva === 2,
            'bg-danger': reserva.EstadoReserva === 3
          }">
            {{ ['Pendiente', 'En curso', 'Finalizada', 'Cancelada'][reserva.EstadoReserva] }}
          </span>
        </p>
      </div>

      <!-- Mensaje si no se puede editar -->
      <div v-if="reserva && !puedeEditar" class="alert alert-warning">
        Solo se pueden editar reservas en estado <strong>Pendiente</strong> o <strong>En Curso</strong>.
      </div>

      <div v-if="reserva && puedeEditar">
        <!-- ==================== SECCIÓN 1: FECHAS ==================== -->
        <div class="card mb-4">
          <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h5 class="mb-0">📅 Editar Fechas</h5>
          </div>
          <div class="card-body">
            <!-- Fechas actuales -->
            <div class="row mb-3">
              <div class="col-md-4">
                <p class="mb-1"><strong>Llegada actual:</strong> {{ formatearFecha(reserva.FechaLlegada) }}</p>
              </div>
              <div class="col-md-4">
                <p class="mb-1"><strong>Salida actual:</strong> {{ formatearFecha(reserva.FechaSalida) }}</p>
              </div>
              <div class="col-md-4">
                <p class="mb-1"><strong>Noches actuales:</strong> {{ nochesActuales }}</p>
              </div>
            </div>

            <!-- Nuevas fechas -->
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label"><strong>Nueva Fecha de Llegada</strong></label>
                <input type="date" class="form-control" v-model="nuevaFechaLlegada" @change="calcularPreviewFechas" />
              </div>
              <div class="col-md-6">
                <label class="form-label"><strong>Nueva Fecha de Salida</strong></label>
                <input type="date" class="form-control" v-model="nuevaFechaSalida" :min="minFechaSalida" @change="calcularPreviewFechas" />
              </div>
            </div>

            <!-- Preview de cambio de fechas -->
            <div v-if="nuevasNoches > 0 && fechasCambiaron" class="mt-3 p-3 border rounded bg-white">
              <h6 class="mb-2">Vista previa</h6>
              <div class="row">
                <div class="col-md-4">
                  <p class="mb-1"><strong>Noches actuales:</strong> {{ nochesActuales }}</p>
                </div>
                <div class="col-md-4">
                  <p class="mb-1"><strong>Nuevas noches:</strong> {{ nuevasNoches }}</p>
                </div>
                <div class="col-md-4">
                  <p class="mb-1">
                    <strong>Diferencia:</strong>
                    <span :class="nuevasNoches > nochesActuales ? 'text-danger' : 'text-success'">
                      {{ nuevasNoches > nochesActuales ? '+' : '' }}{{ nuevasNoches - nochesActuales }} noches
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div class="mt-3">
              <button
                class="btn btn-primary"
                @click="abrirModalConfirmacionFechas"
                :disabled="!fechasCambiaron || !fechasValidas || guardandoFechas"
              >
                <span v-if="guardandoFechas" class="spinner-border spinner-border-sm me-1"></span>
                📅 Actualizar Fechas
              </button>
            </div>
          </div>
        </div>

        <!-- ==================== SECCIÓN 1.5: FECHAS POR HABITACIÓN ==================== -->
        <div v-if="reserva.habitaciones_reservadas.length > 1" class="card mb-4">
          <div class="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
            <h5 class="mb-0">🛏 Editar Fechas por Habitación</h5>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-3">
              Cambia las fechas de una habitación específica sin afectar las demás. Útil cuando una habitación tiene una estancia más corta o diferente.
            </p>

            <!-- Selector de habitación -->
            <div class="mb-3">
              <label class="form-label"><strong>Seleccionar habitación</strong></label>
              <select v-model="habitacionSeleccionadaParaFechas" class="form-select">
                <option value="">-- Elige una habitación --</option>
                <option v-for="hab in reserva.habitaciones_reservadas" :key="hab.IdReservaHabitacion" :value="hab.IdReservaHabitacion">
                  Hab. {{ hab.habitacion?.Numero }} ({{ hab.habitacion?.tipo?.Nombre }}) — {{ nochesHabitacion(hab) }} noches
                </option>
              </select>
            </div>

            <!-- Formulario visible al seleccionar una habitación -->
            <div v-if="habitacionSeleccionadaParaFechas">
              <!-- Fechas actuales de la habitación -->
              <div class="row mb-3">
                <div class="col-md-4">
                  <p class="mb-1"><strong>Llegada actual:</strong> {{ formatearFecha(fechaLlegadaHabitacionActual) }}</p>
                </div>
                <div class="col-md-4">
                  <p class="mb-1"><strong>Salida actual:</strong> {{ formatearFecha(fechaSalidaHabitacionActual) }}</p>
                </div>
                <div class="col-md-4">
                  <p class="mb-1"><strong>Noches actuales:</strong> {{ nochesHabitacionActual }}</p>
                </div>
              </div>

              <!-- Nuevas fechas -->
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label"><strong>Nueva Fecha de Llegada</strong></label>
                  <input type="date" class="form-control" v-model="nuevaFechaLlegadaHabitacion" />
                </div>
                <div class="col-md-6">
                  <label class="form-label"><strong>Nueva Fecha de Salida</strong></label>
                  <input type="date" class="form-control" v-model="nuevaFechaSalidaHabitacion" :min="minFechaSalidaHabitacion" />
                </div>
              </div>

              <!-- Preview del cambio -->
              <div v-if="nuevasNochesHabitacion > 0 && fechasHabitacionCambiaron" class="mt-3 p-3 border rounded bg-white">
                <h6 class="mb-2">Vista previa</h6>
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Noches actuales:</strong> {{ nochesHabitacionActual }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Nuevas noches:</strong> {{ nuevasNochesHabitacion }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1">
                      <strong>Diferencia:</strong>
                      <span :class="nuevasNochesHabitacion > nochesHabitacionActual ? 'text-danger' : 'text-success'">
                        {{ nuevasNochesHabitacion > nochesHabitacionActual ? '+' : '' }}{{ nuevasNochesHabitacion - nochesHabitacionActual }} noches
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <button
                  class="btn btn-warning text-dark"
                  @click="abrirModalConfirmacionFechasHabitacion"
                  :disabled="!fechasHabitacionCambiaron || !fechasHabitacionValidas || guardandoFechasHabitacion"
                >
                  <span v-if="guardandoFechasHabitacion" class="spinner-border spinner-border-sm me-1"></span>
                  🛏 Actualizar Fechas de Habitación
                </button>
                <span v-if="!fechasHabitacionCambiaron && habitacionSeleccionadaParaFechas" class="text-muted ms-2 small">Sin cambios pendientes</span>
              </div>
            </div>

            <div v-else class="text-muted small">Selecciona una habitación para editar sus fechas.</div>
          </div>
        </div>

        <!-- ==================== SECCIÓN 2: PERSONAS ==================== -->
        <div class="card mb-4">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">👥 Editar Personas por Habitación</h5>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-3">
              Cambia la cantidad de personas alojadas en cada habitación. El precio se recalculará automáticamente según la tarifa correspondiente.
            </p>
            <table class="table table-sm table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Habitación</th>
                  <th>Tipo</th>
                  <th>Personas actuales</th>
                  <th>Nueva cantidad</th>
                  <th>Total actual</th>
                  <th>Total estimado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="hab in reserva.habitaciones_reservadas" :key="hab.IdReservaHabitacion">
                  <td><strong>{{ hab.habitacion?.Numero }}</strong></td>
                  <td>{{ hab.habitacion?.tipo?.Nombre || '—' }}</td>
                  <td class="text-center">{{ hab.CantidadPersonasAlojadas }}</td>
                  <td>
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      style="width:80px"
                      v-model.number="personasEdicion[hab.IdReservaHabitacion]"
                      min="1"
                      max="20"
                    />
                  </td>
                  <td>Q{{ Number(hab.TotalHabitacion).toFixed(2) }}</td>
                  <td>
                    <span v-if="tarifasPreview[hab.IdReservaHabitacion] !== undefined">
                      Q{{ Number(tarifasPreview[hab.IdReservaHabitacion]).toFixed(2) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="mt-3">
              <button
                class="btn btn-success"
                @click="abrirModalConfirmacionPersonas"
                :disabled="!personasCambiaron || guardandoPersonas"
              >
                <span v-if="guardandoPersonas" class="spinner-border spinner-border-sm me-1"></span>
                👥 Actualizar Personas y Precios
              </button>
              <span v-if="!personasCambiaron" class="text-muted ms-2 small">Sin cambios pendientes</span>
            </div>
          </div>
        </div>

        <!-- ==================== SECCIÓN 3: EDITAR PERSONAS POR DÍA ==================== -->
        <div class="card mb-4">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">📅 Editar Personas por Día</h5>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-3">
              Ajusta la cantidad de personas para cada día específico (útil si alguien se retira durante la estancia).
            </p>

            <!-- Selector de habitación (si hay múltiples) -->
            <div v-if="reserva.habitaciones_reservadas.length > 1" class="mb-3">
              <label class="form-label"><strong>Seleccionar habitación</strong></label>
              <select v-model="habitacionSeleccionadaParaDias" class="form-select">
                <option value="">-- Elige una habitación --</option>
                <option v-for="hab in reserva.habitaciones_reservadas" :key="hab.IdReservaHabitacion" :value="hab.IdReservaHabitacion">
                  Hab. {{ hab.habitacion?.Numero }} ({{ hab.habitacion?.tipo?.Nombre }}) — {{ hab.CantidadPersonasAlojadas }} personas
                </option>
              </select>
            </div>

            <!-- Tabla de días para la habitación seleccionada -->
            <div v-if="habitacionSeleccionadaParaDias && diasHabitacionActual.length > 0">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Día</th>
                    <th>Personas actual</th>
                    <th>Personas nuevas</th>
                    <th>Tarifa</th>
                    <th>Monto actual</th>
                    <th>Monto estimado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dia in diasHabitacionActual" :key="dia.IdDetalleConsumoHospedaje" :class="dia.cambio ? 'table-warning' : ''">
                    <td>{{ formatearFecha(dia.Fecha) }}</td>
                    <td class="text-center">{{ dia.nombreDia }}</td>
                    <td class="text-center">{{ dia.personasActual }}</td>
                    <td class="text-center">
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        style="width:70px;margin:0 auto"
                        v-model.number="dia.personasNuevas"
                        min="1"
                        max="20"
                        :disabled="dia.esPasado"
                        @change="recalcularMontosPorDia"
                      />
                      <small v-if="dia.esPasado" class="text-muted d-block mt-1">Día pasado</small>
                    </td>
                    <td v-if="dia.tarifa" class="text-center small">Q{{ Number(dia.tarifa).toFixed(2) }}</td>
                    <td v-else class="text-center text-muted small">—</td>
                    <td class="text-center">Q{{ Number(dia.montoActual).toFixed(2) }}</td>
                    <td class="text-center" :class="dia.montoEstimado !== dia.montoActual ? 'fw-bold text-info' : ''">
                      Q{{ Number(dia.montoEstimado).toFixed(2) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Resumen -->
              <div class="mt-3 p-3 border rounded bg-light">
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Total actual:</strong> Q{{ totalActualHabitacionSeleccionada.toFixed(2) }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Total estimado:</strong> Q{{ totalEstimadoHabitacionSeleccionada.toFixed(2) }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1">
                      <strong>Diferencia:</strong>
                      <span :class="totalEstimadoHabitacionSeleccionada < totalActualHabitacionSeleccionada ? 'text-success' : 'text-danger'">
                        Q{{ (totalEstimadoHabitacionSeleccionada - totalActualHabitacionSeleccionada).toFixed(2) }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <button
                  class="btn btn-info text-white"
                  @click="abrirModalConfirmacionPersonasPorDia"
                  :disabled="!hayChangesPorDia || guardandoPersonasPorDia"
                >
                  <span v-if="guardandoPersonasPorDia" class="spinner-border spinner-border-sm me-1"></span>
                  📅 Aplicar Cambios de Personas por Día
                </button>
                <span v-if="!hayChangesPorDia" class="text-muted ms-2 small">Sin cambios pendientes</span>
              </div>
            </div>

            <div v-else-if="habitacionSeleccionadaParaDias === '' && reserva.habitaciones_reservadas.length > 1" class="text-muted small">
              Selecciona una habitación para editar personas por día.
            </div>

            <div v-else-if="habitacionSeleccionadaParaDias && diasHabitacionActual.length === 0" class="text-muted small">
              No hay días disponibles para esta habitación.
            </div>
          </div>
        </div>

        <!-- ==================== RESUMEN HABITACIONES ==================== -->
        <div class="card mb-4">
          <div class="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
            <h5 class="mb-0">🛏 Resumen de Habitaciones</h5>
            <button class="btn btn-sm btn-light" @click="abrirModalAgregarHabitacion">
              <i class="bi bi-plus-circle me-1"></i>Agregar habitación
            </button>
          </div>
          <div class="card-body p-0">
            <table class="table table-sm table-bordered mb-0">
              <thead class="table-light">
                <tr>
                  <th>Habitación</th>
                  <th>Tipo</th>
                  <th>Personas</th>
                  <th>Noches</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="hab in reserva.habitaciones_reservadas" :key="hab.IdReservaHabitacion">
                  <td>{{ hab.habitacion?.Numero }}</td>
                  <td>{{ hab.habitacion?.tipo?.Nombre || '—' }}</td>
                  <td>{{ hab.CantidadPersonasAlojadas }}</td>
                  <td>{{ nochesHabitacion(hab) }}</td>
                  <td>Q{{ Number(hab.TotalHabitacion).toFixed(2) }}</td>
                  <td class="text-center">
                    <button
                      v-if="reserva.habitaciones_reservadas.length > 1"
                      class="btn btn-sm btn-outline-danger bg-red-500 h-6 text-white text-center text-xxs pt-0"
                      @click="quitarHabitacion(hab)"
                      :disabled="quitandoHabitacion"
                    >
                      x
                    </button>
                    <span v-else class="text-muted small">—</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-light fw-bold">
                  <td colspan="5" class="text-end">Total reserva:</td>
                  <td>Q{{ Number(reserva.TotalReserva).toFixed(2) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div><!-- /puedeEditar -->

      <!-- Loading -->
      <div v-if="!reserva" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
        <p class="mt-2">Cargando reserva...</p>
      </div>
    </div>

    <!-- ===== Modal Confirmación Fechas ===== -->
    <div v-if="mostrarModalFechas" class="modal fade show d-block" tabindex="-1" style="background-color:rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title"><i class="bi bi-calendar-event me-2"></i>Confirmar Cambio de Fechas</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModalFechas"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="bi bi-calendar-range text-primary" style="font-size:3rem;"></i>
            <p class="mt-3 mb-1 fs-5">¿Confirmar cambio de fechas?</p>
            <p class="text-muted mt-2">
              <strong>Huésped:</strong> {{ reserva?.huesped?.Nombre }} {{ reserva?.huesped?.Apellido }}<br>
              <strong>Nueva llegada:</strong> {{ formatearFecha(nuevaFechaLlegada) }}<br>
              <strong>Nueva salida:</strong> {{ formatearFecha(nuevaFechaSalida) }}<br>
              <strong>Noches:</strong> {{ nuevasNoches }}
            </p>
            <p class="text-warning small"><i class="bi bi-exclamation-triangle me-1"></i>Los precios de todas las habitaciones se recalcularán automáticamente.</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary px-4" @click="cerrarModalFechas" :disabled="guardandoFechas">Cancelar</button>
            <button class="btn btn-primary px-4" @click="guardarFechas" :disabled="guardandoFechas">
              <span v-if="guardandoFechas" class="spinner-border spinner-border-sm me-1"></span>
              {{ guardandoFechas ? 'Guardando...' : 'Sí, Actualizar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal Confirmación Fechas por Habitación ===== -->
    <div v-if="mostrarModalFechasHabitacion" class="modal fade show d-block" tabindex="-1" style="background-color:rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning text-dark">
            <h5 class="modal-title"><i class="bi bi-calendar-event me-2"></i>Confirmar Cambio de Fechas de Habitación</h5>
            <button type="button" class="btn-close" @click="cerrarModalFechasHabitacion" :disabled="guardandoFechasHabitacion"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="bi bi-door-closed text-warning" style="font-size:3rem;"></i>
            <p class="mt-3 mb-1 fs-5">¿Actualizar fechas de esta habitación?</p>
            <p class="text-muted mt-2">
              <strong>Habitación:</strong> Hab. {{ habitacionParaFechasSeleccionada?.habitacion?.Numero }} ({{ habitacionParaFechasSeleccionada?.habitacion?.tipo?.Nombre }})<br>
              <strong>Nueva llegada:</strong> {{ formatearFecha(nuevaFechaLlegadaHabitacion) }}<br>
              <strong>Nueva salida:</strong> {{ formatearFecha(nuevaFechaSalidaHabitacion) }}<br>
              <strong>Noches:</strong> {{ nuevasNochesHabitacion }}
            </p>
            <p class="text-warning small"><i class="bi bi-exclamation-triangle me-1"></i>El precio de esta habitación se recalculará. Las demás habitaciones no se verán afectadas.</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary px-4" @click="cerrarModalFechasHabitacion" :disabled="guardandoFechasHabitacion">Cancelar</button>
            <button class="btn btn-warning text-dark px-4" @click="guardarFechasHabitacion" :disabled="guardandoFechasHabitacion">
              <span v-if="guardandoFechasHabitacion" class="spinner-border spinner-border-sm me-1"></span>
              {{ guardandoFechasHabitacion ? 'Guardando...' : 'Sí, Actualizar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal Confirmación Personas ===== -->
    <div v-if="mostrarModalPersonas" class="modal fade show d-block" tabindex="-1" style="background-color:rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <h5 class="modal-title">👥 Confirmar Cambio de Personas</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModalPersonas"></button>
          </div>
          <div class="modal-body py-3">
            <p class="mb-3">Se actualizarán las siguientes habitaciones:</p>
            <table class="table table-sm table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Habitación</th>
                  <th>Personas actuales</th>
                  <th>Nuevas personas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="hab in habitacionesConCambioPersonas" :key="hab.IdReservaHabitacion">
                  <td>{{ hab.habitacion?.Numero }}</td>
                  <td>{{ hab.CantidadPersonasAlojadas }}</td>
                  <td class="fw-bold text-success">{{ personasEdicion[hab.IdReservaHabitacion] }}</td>
                </tr>
              </tbody>
            </table>
            <p class="text-warning small mt-2"><i class="bi bi-exclamation-triangle me-1"></i>Los precios se recalcularán automáticamente según la tarifa.</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary px-4" @click="cerrarModalPersonas" :disabled="guardandoPersonas">Cancelar</button>
            <button class="btn btn-success px-4" @click="guardarPersonas" :disabled="guardandoPersonas">
              <span v-if="guardandoPersonas" class="spinner-border spinner-border-sm me-1"></span>
              {{ guardandoPersonas ? 'Guardando...' : 'Sí, Actualizar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal Agregar Habitación ===== -->
    <div v-if="modalAgregarVisible" class="modal fade show d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-plus-circle me-2"></i>Agregar Habitación a la Reserva</h5>
            <button class="btn-close" @click="cerrarModalAgregarHabitacion"></button>
          </div>
          <div class="modal-body">
            <div v-if="cargandoHabitaciones" class="text-center py-4">
              <div class="spinner-border text-primary"></div>
              <p class="mt-2 text-muted">Buscando habitaciones disponibles…</p>
            </div>
            <div v-else-if="habitacionesDisponibles.length === 0" class="alert alert-warning">
              No hay habitaciones disponibles para las fechas de esta reserva.
            </div>
            <div v-else>
              <p class="text-muted small mb-3">
                Habitaciones disponibles del <strong>{{ formatearFecha(reserva.FechaLlegada) }}</strong>
                al <strong>{{ formatearFecha(reserva.FechaSalida) }}</strong>
              </p>
              <div class="row g-3 mb-3">
                <div v-for="hab in habitacionesDisponibles" :key="hab.IdHabitacion" class="col-md-6">
                  <div
                    class="card p-3 h-100"
                    :class="habSeleccionada?.IdHabitacion === hab.IdHabitacion ? 'border-primary bg-primary bg-opacity-10' : ''"
                    role="button"
                    style="cursor:pointer"
                    @click="habSeleccionada = hab"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <i class="bi bi-door-closed fs-5" :class="habSeleccionada?.IdHabitacion === hab.IdHabitacion ? 'text-primary' : 'text-muted'"></i>
                      <div>
                        <strong>Hab. {{ hab.Numero }}</strong> — {{ hab.tipo?.Nombre }}
                        <div class="text-muted small">Capacidad: {{ hab.tipo?.CapacidadMaxima || '?' }} personas</div>
                        <div v-if="hab.precio_estimado" class="text-success small fw-semibold">
                          Estimado: Q{{ Number(hab.precio_estimado).toFixed(2) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="habSeleccionada" class="row g-3">
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Personas alojadas</label>
                  <input v-model.number="nuevaPersonas" type="number" min="1" class="form-control" />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Niños</label>
                  <input v-model.number="nuevaNinios" type="number" min="0" class="form-control" />
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarModalAgregarHabitacion">Cancelar</button>
            <button
              class="btn btn-success"
              :disabled="!habSeleccionada || cargandoAgregar"
              @click="confirmarAgregarHabitacion"
            >
              <span v-if="cargandoAgregar" class="spinner-border spinner-border-sm me-2"></span>
              Agregar habitación
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal Confirmar Personas por Día ===== -->
    <div v-if="mostrarModalPersonasPorDia" class="modal fade show d-block" tabindex="-1" style="background-color:rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-info text-white">
            <h5 class="modal-title">📅 Confirmar Cambios de Personas por Día</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModalPersonasPorDia"></button>
          </div>
          <div class="modal-body">
            <p class="mb-3">Se actualizarán los siguientes días:</p>
            <table class="table table-sm table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Personas actuales</th>
                  <th>Personas nuevas</th>
                  <th>Monto actual</th>
                  <th>Monto nuevo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dia in diasConCambios" :key="dia.IdDetalleConsumoHospedaje" class="table-warning">
                  <td>{{ formatearFecha(dia.Fecha) }}</td>
                  <td class="text-center">{{ dia.personasActual }}</td>
                  <td class="text-center fw-bold">{{ dia.personasNuevas }}</td>
                  <td class="text-center">Q{{ Number(dia.montoActual).toFixed(2) }}</td>
                  <td class="text-center fw-bold text-info">Q{{ Number(dia.montoEstimado).toFixed(2) }}</td>
                </tr>
              </tbody>
              <tfoot class="table-light fw-bold">
                <tr>
                  <td colspan="3" class="text-end">Total habitación:</td>
                  <td class="text-center">Q{{ totalActualHabitacionSeleccionada.toFixed(2) }}</td>
                  <td class="text-center">Q{{ totalEstimadoHabitacionSeleccionada.toFixed(2) }}</td>
                </tr>
              </tfoot>
            </table>
            <p class="text-warning small mt-3"><i class="bi bi-exclamation-triangle me-1"></i>Los totales de la reserva y saldo pendiente se recalcularán automáticamente.</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary px-4" @click="cerrarModalPersonasPorDia" :disabled="guardandoPersonasPorDia">Cancelar</button>
            <button class="btn btn-info text-white px-4" @click="guardarPersonasPorDia" :disabled="guardandoPersonasPorDia">
              <span v-if="guardandoPersonasPorDia" class="spinner-border spinner-border-sm me-1"></span>
              {{ guardandoPersonasPorDia ? 'Guardando...' : 'Sí, Aplicar Cambios' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Modal Confirmar Quitar Habitación ===== -->
    <div v-if="modalQuitarVisible" class="modal fade show d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title"><i class="bi bi-exclamation-triangle me-2"></i>Quitar Habitación</h5>
            <button type="button" class="btn-close btn-close-white" @click="cerrarModalQuitar" :disabled="quitandoHabitacion"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="bi bi-door-closed text-danger" style="font-size:3rem;"></i>
            <p class="mt-3 mb-1 fs-5">¿Quitar esta habitación de la reserva?</p>
            <p class="text-muted">
              <strong>Hab. {{ habAQuitar?.habitacion?.Numero }}</strong>
              — {{ habAQuitar?.habitacion?.tipo?.Nombre }}
            </p>
            <p class="text-warning small"><i class="bi bi-exclamation-triangle me-1"></i>Esta acción eliminará también el consumo de hospedaje asociado a estas fechas.</p>

            <div v-if="requiereReasignacionHabitacion" class="alert alert-warning text-start mt-3">
              <i class="bi bi-exclamation-triangle-fill me-1"></i>
              Esta habitación ya tiene pagos o consumos de restaurante registrados.
              Seleccione a qué habitación de la reserva se deben trasladar antes de quitarla.
              <select class="form-select mt-2" v-model="habitacionDestinoReasignacion">
                <option value="" disabled>Seleccione una habitación destino</option>
                <option
                  v-for="h in habitacionesDestinoReasignables"
                  :key="h.IdReservaHabitacion"
                  :value="h.IdReservaHabitacion"
                >
                  Hab. {{ h.habitacion?.Numero }} — {{ h.habitacion?.tipo?.Nombre }}
                </option>
              </select>
            </div>
          </div>
          <div class="modal-footer justify-content-center">
            <button class="btn btn-secondary px-4" @click="cerrarModalQuitar" :disabled="quitandoHabitacion">Cancelar</button>
            <button
              class="btn btn-danger px-4"
              @click="confirmarQuitarHabitacion"
              :disabled="quitandoHabitacion || (requiereReasignacionHabitacion && !habitacionDestinoReasignacion)"
            >
              <span v-if="quitandoHabitacion" class="spinner-border spinner-border-sm me-1"></span>
              {{ quitandoHabitacion ? 'Quitando...' : 'Sí, quitar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index:1055;">
      <div v-if="mostrarToast" class="toast show text-white" :class="toastColor">
        <div class="d-flex">
          <div class="toast-body">
            <i :class="iconoToast" class="me-2"></i> {{ alerta.mensaje }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="cerrarToast"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '@/config/config.js';

export default {
  name: 'ValidarDiaExtraF',
  data() {
    return {
      reserva: null,
      // Fechas
      nuevaFechaLlegada: '',
      nuevaFechaSalida: '',
      guardandoFechas: false,
      mostrarModalFechas: false,
      // Personas
      personasEdicion: {},  // { IdReservaHabitacion: cantidad }
      tarifasPreview: {},   // { IdReservaHabitacion: totalEstimado }
      guardandoPersonas: false,
      mostrarModalPersonas: false,
      // Toast
      alerta: { mensaje: '', tipo: 'success' },
      mostrarToast: false,
      // Agregar/quitar habitación
      modalAgregarVisible: false,
      habitacionesDisponibles: [],
      cargandoHabitaciones: false,
      habSeleccionada: null,
      nuevaPersonas: 1,
      nuevaNinios: 0,
      cargandoAgregar: false,
      quitandoHabitacion: false,
      modalQuitarVisible: false,
      habAQuitar: null,
      requiereReasignacionHabitacion: false,
      habitacionDestinoReasignacion: '',
      // Personas por día
      habitacionSeleccionadaParaDias: '',
      diasHabitacion: {}, // { IdReservaHabitacion: [ {dias...} ] }
      mostrarModalPersonasPorDia: false,
      guardandoPersonasPorDia: false,
      // Editar fechas por habitación (multi-habitación)
      habitacionSeleccionadaParaFechas: '',
      nuevaFechaLlegadaHabitacion: '',
      nuevaFechaSalidaHabitacion: '',
      guardandoFechasHabitacion: false,
      mostrarModalFechasHabitacion: false,
    };
  },
  async mounted() {
    await this.cargarReserva(this.$route.params.id);
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) this.cargarReserva(newId);
    },
    personasEdicion: {
      deep: true,
      handler() {
        this.actualizarPreviewTarifas();
      }
    },
    habitacionSeleccionadaParaDias(newId) {
      if (newId) {
        this.cargarDiasHabitacion(newId);
        this.recalcularMontosPorDia();
      }
    },
    habitacionSeleccionadaParaFechas(newId) {
      if (newId) {
        this.inicializarFechasHabitacion();
      } else {
        this.nuevaFechaLlegadaHabitacion = '';
        this.nuevaFechaSalidaHabitacion = '';
      }
    }
  },
  computed: {
    puedeEditar() {
      return this.reserva && [0, 1].includes(this.reserva.EstadoReserva);
    },
    habitacionesDestinoReasignables() {
      if (!this.reserva || !this.habAQuitar) return [];
      return this.reserva.habitaciones_reservadas.filter(
        (h) => h.IdReservaHabitacion !== this.habAQuitar.IdReservaHabitacion
      );
    },
    nochesActuales() {
      if (!this.reserva) return 0;
      const llegada = new Date(this.reserva.FechaLlegada);
      const salida = new Date(this.reserva.FechaSalida);
      const diff = Math.round((salida - llegada) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    },
    nuevasNoches() {
      if (!this.nuevaFechaLlegada || !this.nuevaFechaSalida) return 0;
      const llegada = new Date(this.nuevaFechaLlegada);
      const salida = new Date(this.nuevaFechaSalida);
      const diff = Math.round((salida - llegada) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    },
    minFechaSalida() {
      if (!this.nuevaFechaLlegada) return '';
      const d = new Date(this.nuevaFechaLlegada);
      d.setDate(d.getDate() + 1);
      return d.toISOString().split('T')[0];
    },
    fechasCambiaron() {
      if (!this.reserva || !this.nuevaFechaLlegada || !this.nuevaFechaSalida) return false;
      const llegadaActual = this.reserva.FechaLlegada.split('T')[0].split(' ')[0];
      const salidaActual = this.reserva.FechaSalida.split('T')[0].split(' ')[0];
      return this.nuevaFechaLlegada !== llegadaActual || this.nuevaFechaSalida !== salidaActual;
    },
    fechasValidas() {
      if (!this.nuevaFechaLlegada || !this.nuevaFechaSalida) return false;
      return new Date(this.nuevaFechaSalida) > new Date(this.nuevaFechaLlegada);
    },
    personasCambiaron() {
      if (!this.reserva) return false;
      return (this.reserva.habitaciones_reservadas || []).some(hab => {
        const nuevo = this.personasEdicion[hab.IdReservaHabitacion];
        return nuevo !== undefined && nuevo !== hab.CantidadPersonasAlojadas;
      });
    },
    habitacionesConCambioPersonas() {
      if (!this.reserva) return [];
      return (this.reserva.habitaciones_reservadas || []).filter(hab => {
        const nuevo = this.personasEdicion[hab.IdReservaHabitacion];
        return nuevo !== undefined && nuevo !== hab.CantidadPersonasAlojadas;
      });
    },
    toastColor() {
      return { success: 'bg-success', danger: 'bg-danger', warning: 'bg-warning text-dark', info: 'bg-info text-dark' }[this.alerta.tipo] || 'bg-primary';
    },
    iconoToast() {
      return { success: 'bi bi-check-circle-fill', danger: 'bi bi-x-circle-fill', warning: 'bi bi-exclamation-triangle-fill', info: 'bi bi-info-circle-fill' }[this.alerta.tipo] || 'bi bi-info-circle-fill';
    },
    diasHabitacionActual() {
      if (!this.habitacionSeleccionadaParaDias) return [];
      return this.diasHabitacion[this.habitacionSeleccionadaParaDias] || [];
    },
    hayChangesPorDia() {
      return this.diasHabitacionActual.some(dia => dia.personasNuevas !== dia.personasActual);
    },
    diasConCambios() {
      return this.diasHabitacionActual.filter(dia => dia.personasNuevas !== dia.personasActual);
    },
    totalActualHabitacionSeleccionada() {
      return this.diasHabitacionActual.reduce((sum, dia) => sum + dia.montoActual, 0);
    },
    totalEstimadoHabitacionSeleccionada() {
      return this.diasHabitacionActual.reduce((sum, dia) => sum + dia.montoEstimado, 0);
    },
    // Editar fechas por habitación
    habitacionParaFechasSeleccionada() {
      if (!this.reserva || !this.habitacionSeleccionadaParaFechas) return null;
      return (this.reserva.habitaciones_reservadas || []).find(h => h.IdReservaHabitacion == this.habitacionSeleccionadaParaFechas);
    },
    fechaLlegadaHabitacionActual() {
      const hab = this.habitacionParaFechasSeleccionada;
      if (!hab) return '';
      return hab.CheckIn || this.reserva.FechaLlegada;
    },
    fechaSalidaHabitacionActual() {
      const hab = this.habitacionParaFechasSeleccionada;
      if (!hab) return '';
      return hab.CheckOut || this.reserva.FechaSalida;
    },
    nochesHabitacionActual() {
      if (!this.fechaLlegadaHabitacionActual || !this.fechaSalidaHabitacionActual) return 0;
      const diff = Math.round((new Date(this.fechaSalidaHabitacionActual) - new Date(this.fechaLlegadaHabitacionActual)) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    },
    nuevasNochesHabitacion() {
      if (!this.nuevaFechaLlegadaHabitacion || !this.nuevaFechaSalidaHabitacion) return 0;
      const diff = Math.round((new Date(this.nuevaFechaSalidaHabitacion) - new Date(this.nuevaFechaLlegadaHabitacion)) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    },
    minFechaSalidaHabitacion() {
      if (!this.nuevaFechaLlegadaHabitacion) return '';
      const d = new Date(this.nuevaFechaLlegadaHabitacion);
      d.setDate(d.getDate() + 1);
      return d.toISOString().split('T')[0];
    },
    fechasHabitacionCambiaron() {
      if (!this.nuevaFechaLlegadaHabitacion || !this.nuevaFechaSalidaHabitacion) return false;
      const llegadaActual = this.fechaLlegadaHabitacionActual.split('T')[0].split(' ')[0];
      const salidaActual = this.fechaSalidaHabitacionActual.split('T')[0].split(' ')[0];
      return this.nuevaFechaLlegadaHabitacion !== llegadaActual || this.nuevaFechaSalidaHabitacion !== salidaActual;
    },
    fechasHabitacionValidas() {
      if (!this.nuevaFechaLlegadaHabitacion || !this.nuevaFechaSalidaHabitacion) return false;
      return new Date(this.nuevaFechaSalidaHabitacion) > new Date(this.nuevaFechaLlegadaHabitacion);
    }
  },
  methods: {
    async cargarReserva(id) {
      this.reserva = null;
      try {
        const res = await axios.get(`${API_URL}/reservas/detalle/${id}`);
        this.reserva = res.data.reserva;

        // detalles_consumo ya viene incluido en la respuesta de verDetalle() (relación eager-loaded)
        for (const hab of (this.reserva.habitaciones_reservadas || [])) {
          if (!hab.detalles_consumo) {
            hab.detalles_consumo = [];
          }
        }

        // Inicializar fechas con los valores actuales
        this.nuevaFechaLlegada = this.reserva.FechaLlegada.split('T')[0].split(' ')[0];
        this.nuevaFechaSalida = this.reserva.FechaSalida.split('T')[0].split(' ')[0];
        // Inicializar personas con los valores actuales
        this.personasEdicion = {};
        (this.reserva.habitaciones_reservadas || []).forEach(hab => {
          this.personasEdicion[hab.IdReservaHabitacion] = hab.CantidadPersonasAlojadas;
        });
        this.tarifasPreview = {};
        this.habitacionSeleccionadaParaDias = '';
        this.diasHabitacion = {};
        this.habitacionSeleccionadaParaFechas = '';
        this.nuevaFechaLlegadaHabitacion = '';
        this.nuevaFechaSalidaHabitacion = '';
      } catch (error) {
        this.mostrarToastMensaje('Error al cargar la reserva.', 'danger');
      }
    },
    calcularPreviewFechas() {
      // La vista previa se calcula automáticamente con los computed
    },
    actualizarPreviewTarifas() {
      // El preview de tarifas requeriría llamar al backend; por ahora se limpia si cambia
      // ya que no tenemos todas las tarifas localmente
      this.tarifasPreview = {};
    },
    // ===== FECHAS =====
    abrirModalConfirmacionFechas() {
      this.mostrarModalFechas = true;
    },
    cerrarModalFechas() {
      this.mostrarModalFechas = false;
    },
    async guardarFechas() {
      this.guardandoFechas = true;
      try {
        const res = await axios.put(`${API_URL}/reserva/${this.reserva.IdReserva}/editar-fechas`, {
          nueva_fecha_llegada: this.nuevaFechaLlegada,
          nueva_fecha_salida: this.nuevaFechaSalida,
        });
        this.cerrarModalFechas();
        let mensaje = res.data.message;
        if (res.data.nuevo_total !== undefined) {
          mensaje += ` Nuevo total: Q${Number(res.data.nuevo_total).toFixed(2)}`;
        }
        if (res.data.monto_a_devolver) {
          mensaje += ` | A devolver: Q${Number(res.data.monto_a_devolver).toFixed(2)}`;
        }
        this.mostrarToastMensaje(mensaje, 'success');
        await this.cargarReserva(this.reserva.IdReserva);
      } catch (error) {
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al actualizar las fechas.', 'danger');
      } finally {
        this.guardandoFechas = false;
      }
    },
    // ===== PERSONAS =====
    abrirModalConfirmacionPersonas() {
      if (!this.personasCambiaron) return;
      // Validar que todos los valores sean >= 1
      for (const hab of this.habitacionesConCambioPersonas) {
        const val = this.personasEdicion[hab.IdReservaHabitacion];
        if (!val || val < 1) {
          this.mostrarToastMensaje('La cantidad de personas debe ser al menos 1.', 'warning');
          return;
        }
      }
      this.mostrarModalPersonas = true;
    },
    cerrarModalPersonas() {
      this.mostrarModalPersonas = false;
    },
    async guardarPersonas() {
      this.guardandoPersonas = true;
      try {
        const habitacionesPayload = this.habitacionesConCambioPersonas.map(hab => ({
          IdReservaHabitacion: hab.IdReservaHabitacion,
          CantidadPersonasAlojadas: this.personasEdicion[hab.IdReservaHabitacion],
        }));
        const res = await axios.put(`${API_URL}/reserva/${this.reserva.IdReserva}/editar-personas`, {
          habitaciones: habitacionesPayload,
        });
        this.cerrarModalPersonas();
        let mensaje = res.data.message;
        if (res.data.nuevo_total !== undefined) {
          mensaje += ` Nuevo total: Q${Number(res.data.nuevo_total).toFixed(2)}`;
        }
        this.mostrarToastMensaje(mensaje, 'success');
        await this.cargarReserva(this.reserva.IdReserva);
      } catch (error) {
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al actualizar personas.', 'danger');
      } finally {
        this.guardandoPersonas = false;
      }
    },
    // ===== AGREGAR / QUITAR HABITACIONES =====
    async abrirModalAgregarHabitacion() {
      this.modalAgregarVisible = true;
      this.habSeleccionada = null;
      this.nuevaPersonas = 1;
      this.nuevaNinios = 0;
      this.cargandoHabitaciones = true;
      try {
        const { data } = await axios.get(`${API_URL}/reservas/${this.reserva.IdReserva}/habitaciones-agregables`);
        this.habitacionesDisponibles = data.habitaciones || [];
      } catch {
        this.habitacionesDisponibles = [];
        this.mostrarToastMensaje('Error al cargar habitaciones disponibles.', 'danger');
      } finally {
        this.cargandoHabitaciones = false;
      }
    },
    cerrarModalAgregarHabitacion() {
      this.modalAgregarVisible = false;
    },
    async confirmarAgregarHabitacion() {
      if (!this.habSeleccionada) return;
      this.cargandoAgregar = true;
      try {
        await axios.post(`${API_URL}/reservas/${this.reserva.IdReserva}/agregar-habitacion`, {
          id_habitacion: this.habSeleccionada.IdHabitacion,
          cantidad_personas_alojadas: this.nuevaPersonas,
          cantidad_ninios: this.nuevaNinios,
        });
        this.cerrarModalAgregarHabitacion();
        await this.cargarReserva(this.reserva.IdReserva);
        this.mostrarToastMensaje('Habitación agregada correctamente.', 'success');
      } catch (err) {
        this.mostrarToastMensaje(err.response?.data?.error || 'Error al agregar la habitación.', 'danger');
      } finally {
        this.cargandoAgregar = false;
      }
    },
    quitarHabitacion(hab) {
      this.habAQuitar = hab;
      this.requiereReasignacionHabitacion = false;
      this.habitacionDestinoReasignacion = '';
      this.modalQuitarVisible = true;
    },
    cerrarModalQuitar() {
      this.modalQuitarVisible = false;
      this.habAQuitar = null;
      this.requiereReasignacionHabitacion = false;
      this.habitacionDestinoReasignacion = '';
    },
    async confirmarQuitarHabitacion() {
      if (!this.habAQuitar || this.quitandoHabitacion) return;
      if (this.requiereReasignacionHabitacion && !this.habitacionDestinoReasignacion) return;

      this.quitandoHabitacion = true;
      try {
        const payload = this.habitacionDestinoReasignacion
          ? { reasignar_a: this.habitacionDestinoReasignacion }
          : {};
        await axios.delete(
          `${API_URL}/reservas/${this.reserva.IdReserva}/quitar-habitacion/${this.habAQuitar.IdReservaHabitacion}`,
          { data: payload },
        );
        this.cerrarModalQuitar();
        await this.cargarReserva(this.reserva.IdReserva);
        this.mostrarToastMensaje('Habitación quitada correctamente.', 'success');
      } catch (err) {
        if (err.response?.data?.requiere_reasignacion) {
          this.requiereReasignacionHabitacion = true;
          if (this.habitacionesDestinoReasignables.length === 1) {
            this.habitacionDestinoReasignacion = this.habitacionesDestinoReasignables[0].IdReservaHabitacion;
          }
          this.mostrarToastMensaje(
            err.response?.data?.error || 'Esta habitación tiene pagos o consumos registrados. Seleccione a dónde reasignarlos.',
            'warning',
          );
        } else {
          this.mostrarToastMensaje(err.response?.data?.error || 'Error al quitar la habitación.', 'danger');
        }
      } finally {
        this.quitandoHabitacion = false;
      }
    },
    // ===== PERSONAS POR DÍA =====
    async cargarDiasHabitacion(idReservaHabitacion) {
      if (!idReservaHabitacion || !this.reserva) return;

      const habitacion = this.reserva.habitaciones_reservadas.find(h => h.IdReservaHabitacion == idReservaHabitacion);
      if (!habitacion) return;

      // Generar fechas de la reserva
      const fechaLlegada = new Date(this.reserva.FechaLlegada);
      const fechaSalida = new Date(this.reserva.FechaSalida);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const dias = [];
      let fechaActual = new Date(fechaLlegada);

      while (fechaActual < fechaSalida) {
        const fechaStr = fechaActual.toISOString().split('T')[0];
        const esPasado = new Date(fechaStr) < hoy;

        // Buscar detalle de consumo para este día
        const detalle = habitacion.detalles_consumo?.find(d => {
          const diaDetalle = d.Fecha.split('T')[0].split(' ')[0];
          return diaDetalle === fechaStr;
        });

        const personasActual = detalle?.CantidadPersonasEseDia ?? habitacion.CantidadPersonasAlojadas;

        const nombreDia = fechaActual.toLocaleDateString('es-ES', { weekday: 'short' });

        dias.push({
          IdDetalleConsumoHospedaje: detalle?.IdDetalleConsumoHospedaje || 0,
          Fecha: fechaStr,
          nombreDia: nombreDia,
          personasActual: personasActual,
          personasNuevas: personasActual,
          montoActual: detalle?.Monto || 0,
          montoEstimado: detalle?.Monto || 0,
          tarifa: null,
          esPasado: esPasado,
          cambio: false,
        });

        fechaActual.setDate(fechaActual.getDate() + 1);
      }

      this.diasHabitacion = { ...this.diasHabitacion, [idReservaHabitacion]: dias };
    },
    async recalcularMontosPorDia() {
      if (!this.habitacionSeleccionadaParaDias || !this.reserva) return;

      const habitacion = this.reserva.habitaciones_reservadas.find(h => h.IdReservaHabitacion == this.habitacionSeleccionadaParaDias);
      if (!habitacion) return;

      // Aquí necesitaríamos las tarifas desde el backend, pero por ahora usamos un cálculo simple
      // Asumimos que la tarifa es: monto_actual / personas_actual
      const diasActuales = this.diasHabitacionActual;

      diasActuales.forEach(dia => {
        if (dia.personasActual > 0 && dia.montoActual > 0) {
          const tarifaPorPersona = dia.montoActual / dia.personasActual;
          dia.tarifa = tarifaPorPersona;
          dia.montoEstimado = dia.personasNuevas > 0 ? tarifaPorPersona * dia.personasNuevas : 0;
        } else {
          dia.montoEstimado = 0;
        }
        dia.cambio = dia.personasNuevas !== dia.personasActual;
      });
    },
    abrirModalConfirmacionPersonasPorDia() {
      if (!this.hayChangesPorDia) return;
      this.mostrarModalPersonasPorDia = true;
    },
    cerrarModalPersonasPorDia() {
      this.mostrarModalPersonasPorDia = false;
    },
    async guardarPersonasPorDia() {
      if (!this.habitacionSeleccionadaParaDias) return;

      const cambiosPorDia = this.diasConCambios.map(dia => ({
        IdDetalleConsumoHospedaje: dia.IdDetalleConsumoHospedaje,
        Fecha: dia.Fecha,
        CantidadPersonas: dia.personasNuevas,
      }));

      if (cambiosPorDia.length === 0) return;

      this.guardandoPersonasPorDia = true;
      try {
        const res = await axios.put(`${API_URL}/reserva/${this.reserva.IdReserva}/editar-personas-por-dia`, {
          habitacion_id: parseInt(this.habitacionSeleccionadaParaDias),
          cambios_por_dia: cambiosPorDia,
        });

        this.cerrarModalPersonasPorDia();

        let mensaje = res.data.message || 'Personas por día actualizadas correctamente.';
        if (res.data.nuevo_saldo_habitacion !== undefined) {
          mensaje += ` Nuevo saldo habitación: Q${Number(res.data.nuevo_saldo_habitacion).toFixed(2)}`;
        }

        this.mostrarToastMensaje(mensaje, 'success');
        await this.cargarReserva(this.reserva.IdReserva);
        this.habitacionSeleccionadaParaDias = '';
      } catch (error) {
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al guardar cambios de personas por día.', 'danger');
      } finally {
        this.guardandoPersonasPorDia = false;
      }
    },
    // ===== EDITAR FECHAS POR HABITACIÓN =====
    nochesHabitacion(hab) {
      const llegada = hab.CheckIn || this.reserva.FechaLlegada;
      const salida = hab.CheckOut || this.reserva.FechaSalida;
      const diff = Math.round((new Date(salida) - new Date(llegada)) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    },
    inicializarFechasHabitacion() {
      if (!this.habitacionParaFechasSeleccionada) return;
      this.nuevaFechaLlegadaHabitacion = this.fechaLlegadaHabitacionActual.split('T')[0].split(' ')[0];
      this.nuevaFechaSalidaHabitacion = this.fechaSalidaHabitacionActual.split('T')[0].split(' ')[0];
    },
    abrirModalConfirmacionFechasHabitacion() {
      this.mostrarModalFechasHabitacion = true;
    },
    cerrarModalFechasHabitacion() {
      this.mostrarModalFechasHabitacion = false;
    },
    async guardarFechasHabitacion() {
      this.guardandoFechasHabitacion = true;
      try {
        const res = await axios.put(`${API_URL}/reserva/${this.reserva.IdReserva}/habitacion/${this.habitacionSeleccionadaParaFechas}/editar-fechas`, {
          nueva_fecha_llegada: this.nuevaFechaLlegadaHabitacion,
          nueva_fecha_salida: this.nuevaFechaSalidaHabitacion,
        });
        this.cerrarModalFechasHabitacion();
        let mensaje = res.data.message;
        if (res.data.nuevo_total_habitacion !== undefined) {
          mensaje += ` Nuevo total: Q${Number(res.data.nuevo_total_habitacion).toFixed(2)}`;
        }
        this.mostrarToastMensaje(mensaje, 'success');
        this.habitacionSeleccionadaParaFechas = '';
        await this.cargarReserva(this.reserva.IdReserva);
      } catch (error) {
        this.mostrarToastMensaje(error.response?.data?.message || 'Error al actualizar las fechas de la habitación.', 'danger');
      } finally {
        this.guardandoFechasHabitacion = false;
      }
    },
    // ===== UTILS =====
    formatearFecha(fecha) {
      if (!fecha) return '';
      const soloFecha = fecha.split('T')[0].split(' ')[0];
      const [year, month, day] = soloFecha.split('-');
      return `${day}/${month}/${year}`;
    },
    mostrarToastMensaje(mensaje, tipo = 'success') {
      this.alerta.mensaje = mensaje;
      this.alerta.tipo = tipo;
      this.mostrarToast = true;
      setTimeout(() => { this.mostrarToast = false; }, 6000);
    },
    cerrarToast() {
      this.mostrarToast = false;
    },
    volver() {
      this.$router.push('/reservas');
    }
  }
};
</script>

<style scoped>
.toast-container { z-index: 1055; }
</style>
